import React from 'react';
import {FormProvider, useForm} from 'react-hook-form';
import {useTranslation} from 'react-i18next';

import {subDays} from 'date-fns/subDays';

import {
  Modal,
  ModalActions,
  ModalCancelButton,
  ModalHeader,
  ModalSubmitButton,
  NewDatePickerField,
  NewTimePickerField,
  showModal,
} from '@edna/components';
import {Stack} from '@edna/components/primitives';

import {Text} from 'src/components/primitives';
import {MAX_CALENDAR_DATE} from 'src/constants';
import {DEFAULT_ACTIVATION_TIME} from 'src/containers/TariffsCatalog';
import {combineDateAndTime, joiResolver} from 'src/utils';

import {useChangeAvailableTariffMutation, useChangeConnectedTariffMutation} from '../api';
import {TAvailableTariff, TConnectedTariff} from '../definitions';
import * as S from './style';

type TFormData = {newDate: Date; newTime: string; plannedDate: Date};

const ChangeDateModal = React.memo<{
  currentDate: string | Date;
  closeModal: TEmptyFunction;
  onSubmit: (nextDate: Date) => void;
  isLoading?: boolean;
}>(({closeModal, onSubmit, currentDate, isLoading}) => {
  const {t} = useTranslation();

  const methods = useForm<TFormData>({
    mode: 'all',
    defaultValues: {plannedDate: new Date(currentDate), newTime: DEFAULT_ACTIVATION_TIME},
    resolver: joiResolver((joi) =>
      joi.object({
        newDate: joi
          .date()
          .required()
          .custom((value, helpers) => {
            const [values] = helpers.state.ancestors;
            const time = values.newTime;
            const fullDate = combineDateAndTime(value, time);

            if (fullDate.getTime() <= new Date().getTime()) {
              return helpers.error('expiredTime');
            }

            return value;
          }),
        newTime: joi
          .string()
          .required()
          .custom((value, helpers) => {
            const [values] = helpers.state.ancestors;
            const date = values.newDate;

            if (!date) {
              return value;
            }

            const fullDate = combineDateAndTime(date, value);

            if (fullDate.getTime() <= new Date().getTime()) {
              return helpers.error('expiredTime');
            }

            return value;
          }),
      }),
    ),
  });

  const newTimeValue = methods.watch('newTime');

  React.useEffect(() => {
    if (!methods.getValues('newDate')) {
      return;
    }

    methods.trigger('newDate');
  }, [newTimeValue]);

  const handleSubmit = React.useCallback(
    ({newDate, newTime}: TFormData) => {
      onSubmit(combineDateAndTime(newDate, newTime));
    },
    [onSubmit, closeModal],
  );

  return (
    <Modal width="430px">
      <FormProvider {...methods}>
        <ModalHeader>{t('Tariffs:changePlannedDateModal.title')}</ModalHeader>
        <Stack size="2">
          <S.Label htmlFor="plannedDate">{t('Tariffs:changePlannedDateModal.plannedDate')}</S.Label>
          <Stack size="2" direction="ROW" isFullWidth>
            <NewDatePickerField noMargin width="200px" name="plannedDate" readOnly />
            <NewTimePickerField noMargin name="plannedDate" readOnly />
          </Stack>
          <Text topIndent="2" bottomIndent="3" secondary>
            {t('Tariffs:changePlannedDateModal.setNewDate')}
          </Text>
          <S.Label htmlFor="newDate">{t('Tariffs:changePlannedDateModal.newDate')}</S.Label>
          <Stack size="2" direction="ROW" isFullWidth>
            <NewDatePickerField
              noMargin
              width="200px"
              name="newDate"
              minDate={subDays(new Date(), 1)}
              maxDate={MAX_CALENDAR_DATE}
            />
            <NewTimePickerField noMargin name="newTime" showError={false} />
          </Stack>
        </Stack>
        <ModalActions>
          <ModalCancelButton onClick={closeModal}>{t('Common:button.back')}</ModalCancelButton>
          <ModalSubmitButton
            onClick={methods.handleSubmit(handleSubmit)}
            disabled={isLoading}
            loading={isLoading}
          >
            {t('Tariffs:changePlannedDateModal.submitButton')}
          </ModalSubmitButton>
        </ModalActions>
      </FormProvider>
    </Modal>
  );
});

ChangeDateModal.displayName = 'ChangeDateModal';

const ChangeDateForPlannedTariffModal = React.memo<{
  closeModal: TEmptyFunction;
  tariff: TAvailableTariff;
}>(({closeModal, tariff}) => {
  const [changeTariff, {isLoading}] = useChangeAvailableTariffMutation();

  const handleSubmit = (activeFrom: Date) => {
    changeTariff({...tariff, activeFrom})
      .unwrap()
      .then(closeModal)
      .catch(() => {});
  };

  return (
    <ChangeDateModal
      currentDate={tariff.activeFrom}
      isLoading={isLoading}
      closeModal={closeModal}
      onSubmit={handleSubmit}
    />
  );
});

ChangeDateForPlannedTariffModal.displayName = 'ChangeDateForPlannedTariffModal';

const ChangeDateForConnectedTariffModal = React.memo<{
  closeModal: TEmptyFunction;
  tariff: TConnectedTariff;
  name: string;
}>(({closeModal, tariff, name}) => {
  const [changeTariff, {isLoading}] = useChangeConnectedTariffMutation();

  const handleSubmit = (activeFrom: Date) => {
    changeTariff({
      activeFrom,
      newAvlTariffId: tariff.selectedTariffId,
      id: tariff.id,
      version: tariff.version,
      name,
    })
      .unwrap()
      .then(closeModal)
      .catch(() => {});
  };

  return (
    <ChangeDateModal
      currentDate={tariff.activeFrom}
      isLoading={isLoading}
      closeModal={closeModal}
      onSubmit={handleSubmit}
    />
  );
});

ChangeDateForConnectedTariffModal.displayName = 'ChangeDateForConnectedTariffModal';

export const showChangeDateForPlannedTariff = (tariff: TAvailableTariff) =>
  showModal((closeModal) => (
    <ChangeDateForPlannedTariffModal closeModal={closeModal} tariff={tariff} />
  ));

export const showChangeDateForConnectedTariff = (tariff: TConnectedTariff, name: string) =>
  showModal((closeModal) => (
    <ChangeDateForConnectedTariffModal closeModal={closeModal} tariff={tariff} name={name} />
  ));
